import {
  Component,
  input,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

@Component({
  selector: 'so-premium-icon-ui',
  standalone: true,
  imports: [CommonModule, SoSvgIconUI],
  template: `
    <div 
      class="so-premium-icon"
      [style.padding.px]="padding()"
      [style.border-radius.px]="borderRadius()"
      [style.background]="gradientStyle()"
      [style.box-shadow]="shadowStyle()">
      <so-svg-icon-ui
        [name]="icon()"
        color="#ffffff"
        [size]="size()" />
    </div>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }

    .so-premium-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-1px);
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoPremiumIconUI {
  // Nombre del ícono (compatible con so-svg-icon)
  icon = input.required<string>();

  // Color base para el degradado (puede ser variable CSS o hex)
  color = input<string>('--so-primary');

  // Tamaño del ícono
  size = input<number>(16);

  // Padding alrededor del ícono
  padding = input<number>(6);

  // Radio de borde del contenedor
  borderRadius = input<number>(8);

  // Computed: convertir color a valor CSS
  private colorValue = computed(() => {
    const color = this.color();
    if (color.startsWith('--')) return `var(${color})`;
    if (color.startsWith('#') || color.startsWith('rgb')) return color;
    return `var(--${color})`;
  });

  // Computed: color hex para cálculos
  // Colores sincronizados con _colors.scss
  private baseColorHex = computed(() => {
    const color = this.color();
    // Si es variable CSS, usamos colores predefinidos de _colors.scss
    if (color.startsWith('--') || !color.startsWith('#')) {
      const colorMap: Record<string, string> = {
        // Primary variants
        '--so-primary': '#002a7f',
        '--so-primary-subtle': '#4852af',
        '--so-primary-soft': '#3b5998',
        '--so-primary-thick': '#000152',
        'so-primary': '#002a7f',
        // Secondary variants
        '--so-secondary': '#a3bd36',
        '--so-secondary-subtle': '#d7f068',
        '--so-secondary-soft-thick': '#9db345',
        '--so-secondary-thick': '#718d00',
        'so-secondary': '#a3bd36',
        // Danger variants
        '--so-danger': '#f44336',
        '--so-danger-subtle': '#ff7961',
        '--so-danger-thick': '#ba000d',
        'so-danger': '#f44336',
        // Warning variants
        '--so-warning': '#fdd835',
        '--so-warning-subtle': '#ffff6b',
        '--so-warning-thick': '#c6a700',
        'so-warning': '#fdd835',
        // Success variants
        '--so-success': '#64dd17',
        '--so-success-subtle': '#9cff57',
        '--so-success-thick': '#1faa00',
        'so-success': '#64dd17',
        // Info variants
        '--so-info': '#00a3eb',
        '--so-info-subtle': '#21a7e2',
        'so-info': '#00a3eb',
        // Other colors
        '--so-pink': '#ef00b7',
        '--so-purple': '#570df8',
        '--so-orange': '#f38c15',
        '--so-green': '#29a69a',
        '--so-blue': '#002A7F',
        '--so-blue-light': '#41b9f0',
        '--so-gray': '#e7e7e7',
        '--so-gray-thick': '#6c757d',
        '--so-neuter': '#c5c5c5',
        '--so-neuter-thick': '#272d3b',
        'so-pink': '#ef00b7',
        'so-purple': '#570df8',
        'so-orange': '#f38c15',
        'so-green': '#29a69a',
        'so-blue': '#002A7F',
        'so-neuter': '#c5c5c5',
      };
      return colorMap[color] || colorMap['--so-primary'];
    }
    return color;
  });

  // Computed: estilo del degradado
  protected gradientStyle = computed(() => {
    const baseColor = this.baseColorHex();
    const lighterColor = this.adjustColor(baseColor, 15); // 15% más claro
    const darkerColor = this.adjustColor(baseColor, -15); // 15% más oscuro
    return `linear-gradient(135deg, ${lighterColor} 0%, ${darkerColor} 100%)`;
  });

  // Computed: estilo de sombra
  protected shadowStyle = computed(() => {
    const baseColor = this.baseColorHex();
    return `0 2px 6px ${this.hexToRgba(baseColor, 0.35)}`;
  });

  // Ajustar brillo del color
  private adjustColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  }

  // Convertir hex a rgba
  private hexToRgba(hex: string, alpha: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const R = (num >> 16) & 255;
    const G = (num >> 8) & 255;
    const B = num & 255;
    return `rgba(${R}, ${G}, ${B}, ${alpha})`;
  }
}
